import { route } from 'preact-router';
import urlJoin from 'url-join';

import { getHomeURL } from '@/helpers';
import { unsetUserData } from '@/redux/slices/user/actions';
import store from '@/redux/store';

const baseUrl = '/api';

export const API_BASE_URL = baseUrl;

export const buildURL = (...args: string[]) => urlJoin(baseUrl, ...args);

export class RequestError extends Error {
  statusCode: number;

  body: any;

  constructor(statusCode: number, body: any) {
    super(`Request failed with status code: ${statusCode}`);
    this.statusCode = statusCode;
    this.body = body;
  }
}

export const authRedirect = (path?: string) => {
  try {
    if (path) {
      route(`${path}${path === '/talk-schedule' ? window.location.search : ''}`, true);
    } else if (localStorage.getItem('returningUser')) {
      route('/login', true);
    } else {
      route(getHomeURL(), true);
    }
  } catch (err) {
    console.warn('Local storage is not available.');
    route(getHomeURL(), true);
  }
};

export const handleResponse = async <T>(response: Response) => {
  if (response.status === 401) {
    store.dispatch(unsetUserData());
    authRedirect();
  }

  if (!response.ok) {
    throw new RequestError(response.status, await response.json());
  }

  if (response.status === 204 || response.status === 201) {
    return undefined as unknown as T;
  }

  return response.json() as Promise<T>;
};

export const post = async <R = any, D = any>(url: string, data?: D) => {
  const res = await fetch(url, {
    method: 'POST',
    body: data != null ? JSON.stringify(data) : undefined,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    credentials: 'include',
  });

  return handleResponse<R>(res);
};

export const patch = async <R = any, D = any>(url: string, data?: D) => {
  const res = await fetch(url, {
    method: 'PATCH',
    body: JSON.stringify(data),
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    credentials: 'include',
  });

  return handleResponse<R>(res);
};

export const callDelete = async <R = any, D = any>(url: string, data?: D) => {
  const res = await fetch(url, {
    method: 'DELETE',
    body: data ? JSON.stringify(data) : undefined,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    credentials: 'include',
  });

  return handleResponse<R>(res);
};

export const get = async <R = any>(url: string) => {
  const res = await fetch(url, {
    headers: {
      Accept: 'application/json',
    },
    credentials: 'include',
  });

  return handleResponse<R>(res);
};

export const put = async <R = any, D = any>(url: string, data?: D) => {
  const res = await fetch(url, {
    method: 'PUT',
    body: JSON.stringify(data),
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    credentials: 'include',
  });

  return handleResponse<R>(res);
};

export const partnerMPLinkFetcher = async () => {
  try {
    const url = buildURL('/zoos/partnerMPLink');
    const data = await get<{ mpLink: string }>(url);
    return data.mpLink;
  } catch (error) {
    console.error('Error fetching partner MPLink:', error);
    return null;
  }
};

export type ErrorsMap<T> = Partial<Record<keyof T | '*', string[]>>;

export const toErrorsMap = <T>(error: any) => {
  const defaultMessage: string = error.body?.error || error.message || 'Something Went Wrong, please try again!';
  const defaultErrors = { '*': [defaultMessage] } as ErrorsMap<T>;
  if (error instanceof RequestError) {
    return (error.body.errors as ErrorsMap<T>) || defaultErrors;
  }
  return defaultErrors;
};
